<template>
  <div id="app" class="log-detail-page" v-wechat-title="titleTop">
    <!-- Immersive Background (Only visible when data is loaded or during loading) -->
    <div class="immersive-bg">
      <img :src="currentBg" class="bg-image" />
      <div class="bg-overlay"></div>
    </div>

    <!-- Header -->
    <div class="page-header">
      <van-icon name="arrow-left" class="nav-icon" @click="goBack" />
      <div class="nav-title font-tech">LOG DETAIL</div>
      <div 
        class="nav-btn" 
        :class="{ 'is-save': isMyLog }" 
        v-if="isMyLog && !isLoading && !isError"
        @click="onEditLog"
      >
        EDIT
      </div>
    </div>

    <!-- 1. Loading State -->
    <div v-if="isLoading" class="state-container loading">
      <van-loading type="spinner" color="#00E5FF" size="40px" vertical>
        <span class="loading-text font-tech">Retrieving Data...</span>
      </van-loading>
    </div>

    <!-- 2. Error State (With Retry) -->
    <div v-else-if="isError" class="state-container error">
      <van-icon name="warning-o" size="60" color="#FF4757" />
      <p class="error-msg font-tech">CONNECTION FAILED</p>
      <p class="error-detail">{{ errorMsg }}</p>
      <van-button 
        round 
        type="info" 
        color="rgba(255, 255, 255, 0.1)" 
        class="retry-btn" 
        @click="init"
      >
        <span style="color: #00E5FF">TRY AGAIN</span>
      </van-button>
    </div>

    <!-- 3. Success Content -->
    <div v-else-if="data" class="content-wrapper">
      
      <!-- Private Log View -->
      <div v-if="data.isPrivate" class="private-rect state-container">
        <van-icon name="lock" size="40" color="#666" />
        <p>This log is set to private.</p>
      </div>

      <!-- Public/My Log View -->
      <div v-else>
        <!-- HUD Hero Section -->
        <div class="hero-hud-wrapper">
          <div class="hero-meta-bar font-tech">
            <span class="meta-date">
              {{ formatDate(data.log.dateOfStar, 'dd MMM yyyy').toUpperCase() }}
            </span>
            <span class="meta-log-id">LOG #{{ data.log.diveNum }}</span>
          </div>
          
          <div class="site-title-area">
            <div class="loc-macro font-tech">
              <van-icon name="location-o" />
              {{ data.log.country || 'UNKNOWN' }} · {{ data.log.areaName || 'AREA' }}
            </div>
            <h1 class="site-name">
              {{ data.log.divePlaceName ? data.log.divePlaceName : data.log.divePlaceNameEng }}
            </h1>
            <div class="sub-site-name" v-if="data.log.shopName">
              @ {{ data.log.shopName }}
            </div>
          </div>

          <div class="hero-stats-grid">
            <div class="hero-stat-item">
              <span class="hs-label">Max Depth</span>
              <div>
                <span class="hs-val">{{ formatNumber(data.log.maxDeep) }}</span>
                <span class="hs-unit">m</span>
              </div>
            </div>
            <div class="hero-stat-item">
              <span class="hs-label">Dive Time</span>
              <div>
                <span class="hs-val">{{ getDuration(data.log) }}</span>
                <span class="hs-unit">min</span>
              </div>
            </div>
          </div>
          
          <!-- Smart Graph -->
          <svg class="graph-container" viewBox="0 0 300 100" preserveAspectRatio="none">
            <defs>
              <linearGradient id="gradArea" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style="stop-color:rgb(0, 229, 255);stop-opacity:0.2" />
                <stop offset="100%" style="stop-color:rgb(0, 229, 255);stop-opacity:0" />
              </linearGradient>
            </defs>
            <g v-for="tick in axisTicks" :key="tick.val">
               <line x1="0" :y1="tick.y" x2="300" :y2="tick.y" class="chart-grid-line" stroke-dasharray="2 2"/>
               <text x="5" :y="tick.y - 4" class="chart-grid-text">{{ tick.val }}m</text>
            </g>
            <path :d="curvePath" class="chart-path-area" />
            <path :d="curvePath" class="chart-path-line" />
          </svg>
        </div>

        <!-- Detail Content -->
        <div class="detail-content">
          <div class="specs-strip">
            <div class="spec-cell">
              <span class="sc-label">Entry Time</span>
              <span class="sc-val">{{ formatDate(data.log.dateOfStar, "hh:mm") }}</span>
            </div>
            <div class="spec-cell">
              <span class="sc-label">Temp</span>
              <div>
                <span class="sc-val">{{ formatNumber(data.log.temparature) }}</span>
                <span style="font-size:12px; color:rgba(255,255,255,0.5)">°C</span>
              </div>
            </div>
            <div class="spec-cell">
              <span class="sc-label">Visibility</span>
              <div>
                <span class="sc-val">{{ formatNumber(data.log.visibility) }}</span>
                <span style="font-size:12px; color:rgba(255,255,255,0.5)">m</span>
              </div>
            </div>
             <div class="spec-cell">
              <span class="sc-label">Avg Depth</span>
              <div>
                <span class="sc-val">{{ formatNumber(data.log.avgDeep) }}</span>
                <span style="font-size:12px; color:rgba(255,255,255,0.5)">m</span>
              </div>
            </div>
          </div>

          <!-- Gallery -->
          <div class="section-header font-tech" v-if="galleryImages.length > 0">
            <van-icon name="photograph" class="section-icon" />
            Visuals
          </div>
          <div class="gallery-row" v-if="galleryImages.length > 0">
            <div class="img-wrapper" v-for="(img, i) in galleryImages" :key="i">
              <img :src="img" class="gallery-img" @click="previewImg(i)">
            </div>
          </div>

          <!-- Team -->
          <div class="section-header font-tech" v-if="data.member && data.member.length > 0">
            <van-icon name="friends" class="section-icon" />
            Team ({{ data.member.length }})
          </div>
          <div class="buddy-list" v-if="data.member && data.member.length > 0">
            <div class="buddy-unit" v-for="member in data.member" :key="member.userId">
              <img :src="member.userIcon || defaultAvatar" class="buddy-face" @error="useDefaultAvatar">
              <span class="buddy-name">{{ member.userName }}</span>
            </div>
          </div>

          <!-- Notes -->
          <div class="section-header font-tech">
            <van-icon name="records" class="section-icon" />
            Log Notes
          </div>
          <div class="notes-box">
             {{ data.log.about || 'No notes for this dive.' }}
          </div>
          
          <!-- Source Log Link -->
          <div class="section-header font-tech" v-if="data.isMyLog && data.log.parentId" style="margin-top: 20px;">
             <van-icon name="link-o" class="section-icon" />
             Source Log
          </div>
          <div class="notes-box link-box" v-if="data.isMyLog && data.log.parentId" @click="onGotoLog(data.log.parentId, data.parentHashCode)">
             Copied from {{ data.parentName }}'s Dive #{{ data.parentNum }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'
import { Button, Image, ImagePreview, Icon, Loading } from 'vant'
import { getUrlKey, ShowFloat, GetEnumText, formatDate, GetDiveTitle } from '@/utils/base'

export default {
  name: 'DiveLogDetail',
  components: {
    [Button.name]: Button,
    [Image.name]: Image,
    [Icon.name]: Icon,
    [Loading.name]: Loading,
  },
  data() {
    return {
      // 核心状态管理 (State Management)
      isLoading: true,  // 控制加载显示
      isError: false,   // 控制错误显示
      errorMsg: '',     // 错误详情

      // 业务数据
      titleTop: '',
      id: undefined,
      code: undefined,
      data: null, // 初始化为 null
      defaultAvatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80',
      defaultBg: 'https://images.unsplash.com/photo-1551244072-5d12893278ab?auto=format&fit=crop&w=800&q=80',
    }
  },
  computed: {
    isMyLog() {
      return this.data && this.data.isMyLog;
    },
    galleryImages() {
      if (!this.data || !this.data.log.res) return [];
      return this.data.log.res.split(',').filter(item => item);
    },
    currentBg() {
      if (this.galleryImages.length > 0) return this.galleryImages[0];
      return this.defaultBg;
    },
    // Graph Calculations
    axisTicks() {
        if (!this.data || !this.data.log.maxDeep) return [];
        const depth = parseFloat(this.data.log.maxDeep);
        if(!depth) return [];
        const height = 100;
        const yMax = height * 0.8; 
        const step = depth < 20 ? 5 : 10;
        const ticks = [];
        for(let val = step; val < depth; val += step) {
            const y = val * (yMax / depth);
            ticks.push({ val, y });
        }
        return ticks;
    },
    curvePath() {
        if (!this.data || !this.data.log.maxDeep) return '';
        const depth = parseFloat(this.data.log.maxDeep);
        if (!depth || depth <= 0) return '';
        const width = 300; 
        const height = 100; 
        const yMax = height * 0.8; 
        const safeDepthPx = 5 * (yMax / depth); 
        const hasSafetyStop = depth > 8; 
        const ySafety = hasSafetyStop ? safeDepthPx : yMax * 0.5;
        const x1 = width*0.1; const x2 = width*0.65; const x3 = width*0.75; const x4 = width*0.9; const x5 = width;
        let path = `M0,0 L${x1},${yMax} L${x2},${yMax}`;
        if (hasSafetyStop) path += ` L${x3},${ySafety} L${x4},${ySafety}`;
        path += ` L${x5},0`;
        return path;
    }
  },
  created() {
    this.titleTop = GetDiveTitle()
    this.id = this.$route.params.id
    this.code = getUrlKey('logcode')
    
    // 如果没有ID，直接报错，避免无意义请求
    if (!this.id) {
        this.isLoading = false;
        this.isError = true;
        this.errorMsg = 'Invalid Log ID';
    } else {
        this.init();
    }
  },
  methods: {
    GetEnumText,
    formatDate,
    formatNumber(val) {
        return val ? ShowFloat(val, 1) : '--';
    },
    getDuration(log) {
        if (log.dateOfLen) return log.dateOfLen;
        if (log.dateOfEnd && log.dateOfStar) {
            const start = new Date(log.dateOfStar).getTime();
            const end = new Date(log.dateOfEnd).getTime();
            const diff = Math.floor((end - start) / 60000);
            return diff > 0 ? diff : '--';
        }
        return '--';
    },
    useDefaultAvatar(e) {
        e.target.src = this.defaultAvatar;
    },

    // --- 核心修复逻辑 (Fix Logic) ---
    init() {
      // 1. 重置状态
      this.isLoading = true;
      this.isError = false;
      this.errorMsg = '';
      this.data = null;

      console.log('Fetching log with ID:', this.id, 'Code:', this.code);

      request({
        url: '/Dive/getDiveLog',
        method: 'post',
        data: {
          id: this.id,
          code: this.code
        }
      }).then(response => {
        // 2. 无论 response.success 是 true 还是 false，请求本身是完成了，所以 loading 结束
        this.isLoading = false;

        if (!response.success) {
          // 3. 业务层面的错误捕获
          this.isError = true;
          this.errorMsg = response.msg || 'Failed to load log data.';
          console.error('API Business Error:', response);
          return;
        }

        // 4. 成功获取数据
        console.log('Log Data Success:', response.data);
        this.data = response.data;

      }).catch(error => {
        // 5. 网络/代码层面的错误捕获
        this.isLoading = false;
        this.isError = true;
        this.errorMsg = error.message || 'Network Error';
        console.error('API Network Error:', error);
      });
    },

    goBack() {
      if (this.$route.query.backUrl) {
           this.$router.push(this.$route.query.backUrl);
      } else {
           this.$router.go(-1);
      }
    },
    onEditLog() {
      if (!this.data || !this.data.isMyLog) return;
      this.$router.push('/dive/logEdit/' + this.id)
    },
    onGotoLog(id, code) {
      this.$router.push('/dive/log/' + id + '?logcode=' + code)
      // Reset logic for component reuse
      this.id = id;
      this.code = code;
      this.init();
    },
    previewImg(index) {
        ImagePreview({
            images: this.galleryImages,
            startPosition: index,
            closeable: true
        });
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;700&family=Roboto:wght@300;400;500;700&display=swap');

/* --- Global Layout --- */
.log-detail-page {
  --bg-deep: #050a14;
  --glass-card: rgba(255, 255, 255, 0.03);
  --border-dim: rgba(255, 255, 255, 0.08); 
  --accent-cyan: #00E5FF;
  --accent-alert: #FF4757;
  --text-white: #ffffff;
  --text-mute: rgba(255, 255, 255, 0.45);
  
  background-color: var(--bg-deep);
  color: var(--text-white);
  font-family: 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
  position: relative;
  padding-bottom: 40px;
}

.font-tech { font-family: 'Oswald', sans-serif; letter-spacing: 1px; text-transform: uppercase; }

/* --- Background --- */
.immersive-bg { position: fixed; top: 0; left: 0; width: 100%; height: 100vh; z-index: 0; background: #000; pointer-events: none; }
.bg-image { width: 100%; height: 100%; object-fit: cover; opacity: 0.4; }
.bg-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(to bottom, rgba(5,10,20,0.3) 0%, var(--bg-deep) 90%); }

/* --- Header --- */
.page-header {
    position: sticky; top: 0; z-index: 100; padding: 12px 20px;
    background: rgba(5, 10, 20, 0.96); backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--border-dim);
    display: flex; justify-content: space-between; align-items: center;
    height: 48px; box-sizing: border-box;
}
.nav-title { font-size: 14px; font-weight: 600; letter-spacing: 2px; color: white; opacity: 0.9; }
.nav-icon { font-size: 20px; color: white; cursor: pointer; }
.nav-btn { font-family: 'Oswald', sans-serif; font-size: 14px; letter-spacing: 1px; font-weight: 500; cursor: pointer; padding: 8px; }
.nav-btn.is-save { color: var(--accent-cyan); text-shadow: 0 0 10px rgba(0,229,255,0.3); }

/* --- State Containers (Loading / Error) --- */
.state-container {
    display: flex; flex-direction: column; justify-content: center; align-items: center;
    min-height: 60vh; position: relative; z-index: 10; text-align: center;
}
.loading-text { margin-top: 15px; color: var(--accent-cyan); font-size: 12px; animation: pulse 1.5s infinite; }
.error-msg { font-size: 20px; font-weight: 700; margin-top: 20px; margin-bottom: 5px; color: white; }
.error-detail { color: var(--text-mute); font-size: 12px; margin-bottom: 30px; max-width: 80%; }
.retry-btn { border: 1px solid var(--accent-cyan) !important; }

@keyframes pulse { 0% { opacity: 0.5; } 50% { opacity: 1; } 100% { opacity: 0.5; } }

/* --- HUD Hero Section --- */
.hero-hud-wrapper {
    position: relative; z-index: 10; margin: 24px 20px 0 20px;
    border: 1px solid var(--border-dim); background: rgba(5, 10, 20, 0.6); backdrop-filter: blur(5px);
}

.hero-meta-bar {
    padding: 12px 16px; border-bottom: 1px solid var(--border-dim);
    display: flex; justify-content: space-between; align-items: center;
}
.meta-date { font-size: 12px; color: var(--accent-cyan); font-weight: 500; letter-spacing: 1px; }
.meta-log-id { font-size: 14px; color: white; font-family: 'Oswald'; letter-spacing: 1px; }

.site-title-area { padding: 20px 16px 10px 16px; }
.loc-macro {
    display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--accent-cyan);
    text-transform: uppercase; letter-spacing: 2px; font-weight: 500; margin-bottom: 8px; opacity: 0.9;
}
.site-name {
    font-size: 34px; line-height: 1.05; font-weight: 700; 
    text-transform: uppercase; font-family: 'Oswald'; color: white; margin: 0;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.sub-site-name { font-size: 12px; color: var(--text-mute); margin-top: 5px; font-style: italic; }

.hero-stats-grid { display: grid; grid-template-columns: 1fr 1fr; border-top: 1px solid var(--border-dim); border-bottom: 1px solid var(--border-dim); }
.hero-stat-item { padding: 16px; display: flex; flex-direction: column; position: relative; }
.hero-stat-item:first-child { border-right: 1px solid var(--border-dim); }
.hs-label { font-size: 10px; color: var(--text-mute); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
.hs-val { font-size: 36px; color: white; font-family: 'Oswald'; line-height: 1; transition: all 0.3s; display: inline-block; }
.hs-unit { font-size: 12px; color: var(--text-mute); font-weight: 300; margin-left: 2px; }

.graph-container { position: relative; height: 120px; width: 100%; background: rgba(0,0,0,0.2); display: block; }
.chart-grid-line { stroke: rgba(255,255,255,0.08); stroke-width: 1; vector-effect: non-scaling-stroke; }
.chart-grid-text { font-size: 9px; fill: rgba(255,255,255,0.3); font-family: 'Roboto'; }
.chart-path-area { fill: url(#gradArea); }
.chart-path-line { fill: none; stroke: var(--accent-cyan); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

/* --- Content --- */
.detail-content { position: relative; z-index: 10; margin: 24px 20px; }

.specs-strip { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; background: var(--glass-card); border: 1px solid var(--border-dim); margin-bottom: 30px; }
.spec-cell { padding: 12px 8px; text-align: center; border-right: 1px solid var(--border-dim); display: flex; flex-direction: column; gap: 2px; }
.spec-cell:last-child { border-right: none; }
.sc-label { font-size: 9px; color: var(--text-mute); text-transform: uppercase; letter-spacing: 0.5px; }
.sc-val { font-size: 16px; color: white; font-family: 'Oswald'; font-weight: 500; }

.section-header { display: flex; align-items: center; gap: 8px; font-size: 11px; color: var(--text-mute); text-transform: uppercase; letter-spacing: 2px; margin-bottom: 12px; padding-left: 4px; }
.section-icon { color: var(--accent-cyan); font-size: 14px; }

.gallery-row { display: flex; gap: 10px; overflow-x: auto; padding-bottom: 10px; margin-bottom: 20px; scrollbar-width: none; min-height: 102px; }
.gallery-row::-webkit-scrollbar { display: none; }
.img-wrapper { position: relative; flex: 0 0 140px; height: 100px; }
.gallery-img { width: 100%; height: 100%; object-fit: cover; border: 1px solid var(--border-dim); transition: 0.3s; }

.buddy-list { display: flex; gap: 16px; margin-bottom: 30px; overflow-x: auto; padding-bottom: 10px; }
.buddy-unit { display: flex; flex-direction: column; align-items: center; gap: 6px; position: relative; min-width: 50px; }
.buddy-face { width: 48px; height: 48px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(255,255,255,0.15); }
.buddy-name { font-size: 10px; color: white; text-transform: uppercase; letter-spacing: 0.5px; }

.notes-box { font-size: 14px; line-height: 1.6; color: rgba(255,255,255,0.85); font-weight: 300; padding: 0 4px; border-left: 2px solid var(--accent-cyan); padding-left: 12px; min-height: 40px; }
.link-box { cursor: pointer; color: var(--accent-cyan); text-decoration: underline; }
</style>